'use client';

import { Cookie } from './constants';
import type { IAppInfo } from './interfaces';

export function getCookie(name: string) {
  const cookies = `; ${document.cookie}`;
  const parts = cookies.split(`; ${name}=`);
  const value = parts.length === 2 ? parts.pop()?.split(';').shift() : null;
  return value ?? null;
}

export function convertImage(
  imageFile: File,
  options?: {
    format: string;
    maxWidth?: number;
    quality?: number;
  },
): Promise<File> {
  const { format = 'image/jpeg', maxWidth = 720, quality } = options ?? {};
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        const newWidth = Math.min(img.width, maxWidth);
        const newHeight = newWidth === img.width ? img.height : newWidth / (img.width / img.height);

        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('No blob created from canvas'));
              return;
            }

            const fileName = imageFile.name.split('.').slice(0, -1).join('.');
            const file = new File([blob], `${fileName}.${format.split('/')[1]}`, {
              type: format,
              lastModified: Date.now(),
            });
            resolve(file);
          },
          format,
          quality,
        );
      } finally {
        URL.revokeObjectURL(img.src);
      }
    };

    img.src = URL.createObjectURL(imageFile);
  });
}

export function getAppInfo(inputQrHash?: string): IAppInfo {
  let appInfo: IAppInfo = { country: 'unknown', channel: 'unknown', supportedLngs: ['en-GB'], userServiceApiKey: '' };
  let qrHash = inputQrHash;
  if (!inputQrHash) qrHash = getCookie(Cookie.currentQr) ?? undefined;
  if (qrHash) {
    const appInfoCookie = getCookie(`appInfo-${qrHash}`);
    if (appInfoCookie) appInfo = JSON.parse(decodeURIComponent(appInfoCookie)) as IAppInfo;
  }
  return appInfo;
}

async function loadPdfJs() {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
  return pdfjs;
}

const MERGED_MAX_WIDTH = 1280;
const MERGED_MAX_HEIGHT = 8192;
const MERGED_QUALITY_START = 0.85;
const MERGED_QUALITY_FLOOR = 0.55;
const MERGED_QUALITY_STEP = 0.1;
const MERGED_MAX_BYTES = 10 * 1024 * 1024;
const MAX_RECEIPT_PHOTOS = 3;

async function loadImageBitmap(source: Blob): Promise<ImageBitmap> {
  return createImageBitmap(source, { imageOrientation: 'from-image' });
}

function drawScaledToCanvas(bitmap: ImageBitmap, maxWidth: number): HTMLCanvasElement {
  const scale = Math.min(1, maxWidth / bitmap.width);
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return canvas;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('No blob created from canvas'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      quality,
    );
  });
}

export async function mergeImages(images: Blob[]): Promise<File> {
  const limited = images.slice(0, MAX_RECEIPT_PHOTOS);
  if (limited.length === 0) throw new Error('No images to merge');

  const layers: HTMLCanvasElement[] = [];
  for (const image of limited) {
    const bitmap = await loadImageBitmap(image);
    try {
      layers.push(drawScaledToCanvas(bitmap, MERGED_MAX_WIDTH));
    } finally {
      bitmap.close();
    }
  }

  const stackedWidth = Math.max(...layers.map((layer) => layer.width));
  const stackedHeight = layers.reduce((total, layer) => total + layer.height, 0);
  const fit = Math.min(1, MERGED_MAX_HEIGHT / stackedHeight);
  const width = Math.round(stackedWidth * fit);
  const height = Math.round(stackedHeight * fit);

  const merged = document.createElement('canvas');
  merged.width = width;
  merged.height = height;
  const ctx = merged.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  let top = 0;
  for (const layer of layers) {
    const layerWidth = Math.round(layer.width * fit);
    const y = Math.round(top);
    const layerHeight = Math.round(top + layer.height * fit) - y;
    ctx.drawImage(layer, Math.round((width - layerWidth) / 2), y, layerWidth, layerHeight);
    top += layer.height * fit;
    layer.width = 0;
    layer.height = 0;
  }

  let quality = MERGED_QUALITY_START;
  let blob = await canvasToJpegBlob(merged, quality);

  while (blob.size > MERGED_MAX_BYTES && quality > MERGED_QUALITY_FLOOR) {
    quality = Math.max(MERGED_QUALITY_FLOOR, quality - MERGED_QUALITY_STEP);
    blob = await canvasToJpegBlob(merged, quality);
  }

  return new File([blob], 'combined.jpeg', { type: 'image/jpeg', lastModified: Date.now() });
}

export async function convertPdfToSingleImage(file: File): Promise<File | null> {
  const pdfjsLib = await loadPdfJs();

  let pdf;
  try {
    const buffer = Buffer.from(await file.arrayBuffer());
    pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
  } catch (error) {
    return null;
  }

  const pages = [];
  let totalHeight = 0;
  let maxWidth = 0;

  const totalPages = pdf.numPages > 5 ? 5 : pdf.numPages;

  for (let i = 1; i <= totalPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d') as unknown as CanvasRenderingContext2D;

    await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    pages.push(canvas);

    totalHeight += viewport.height;
    maxWidth = Math.max(maxWidth, viewport.width);
  }

  const mergedCanvas = document.createElement('canvas');
  mergedCanvas.width = maxWidth;
  mergedCanvas.height = totalHeight;
  const mergedCtx = mergedCanvas.getContext('2d');

  let currentY = 0;
  for (const page of pages) {
    mergedCtx?.drawImage(page, 0, currentY);
    currentY += page.height;
  }

  return new Promise((resolve, reject) => {
    const format = 'image/jpeg';

    mergedCanvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('No blob created from canvas'));
        return;
      }

      const file = new File([blob], 'combined.jpeg', {
        type: format,
        lastModified: Date.now(),
      });
      resolve(file);
    }, format);
  });
}
